import { defineStore } from 'pinia'
import { ref, computed, toRefs } from 'vue'
import useFeatures from './features'
import { Drawing, Orthophoto } from '@/interfaces/misc.interface'
import {
  LineCategoryDto,
  Line,
  LineGeoJsonFeature,
  LineGeoJsonProperties
} from '@/services/lines.service'
import {
  addLineFeaturesForType,
  setExistingLineDataVisibility,
  setNewLineDataVisibility
} from '@/utils/line'
import { Ticket } from '@/services/tickets.service'
import { ZoneProperties } from '@/services/zones.service'

type OldFeatureType =
  'featureInfo' | 'featureAnalytic' | 'featureProjectAnalysis' | 'featureWip' | ''

export const useGeneralStore = defineStore('general', () => {
  const features = useFeatures()
  const { setMainFeature } = features
  const { mainFeatureName } = toRefs(features)
  // States
  const currentOverlayModalPinia = ref<string>('')
  const currentOverlayModalOnModalPinia = ref<string>('') // Modal on modal was a joint decision on prod/tech review 2024-08-19
  const filteredDrawings = ref<Drawing[]>([])
  const filteredLineLayers = ref<Line[]>([])
  const filteredOrthophotos = ref<Orthophoto[]>([])
  const filteredWmsLayers = ref<any[]>([])
  const isFeatureFullscreen = ref<boolean>(false)
  const linesForSelectedRealEstate = ref<LineGeoJsonFeature[]>([])
  const lineCategoriesForSelectedRealEstate = ref<LineCategoryDto[]>([])
  const rightClickCoordinate = ref<number[]>([0, 0])
  const selectedAnalysisKPI = ref<string | undefined>(undefined)
  // entityId of the file currently selected/previewed in the files feature — used to highlight the entity on the map
  const selectedFileEntityId = ref<string | null>(null)
  const selectedLine = ref<LineGeoJsonProperties | null>(null)
  const selectedLineLayerId = ref<string | null>(null)
  const selectedTicket = ref<Ticket | null>(null)
  const selectedZone = ref<ZoneProperties | null>(null)
  const selectedZoneLayerId = ref<string | null>(null)
  const showLineMenu = ref<boolean>(false)
  const showOverlayModalPinia = ref<boolean>(false)
  const showOverlayModalOnModalPinia = ref<boolean>(false)
  const hideFeature = ref<boolean>(false)
  const ticketsList = ref<Ticket[]>([])
  const connectionPopup = ref<boolean>(false)

  const newOldFeatureMap: Record<string, OldFeatureType> = {
    info: 'featureInfo',
    analytics: 'featureAnalytic',
    wip: 'featureWip',
    'project-analysis': 'featureProjectAnalysis'
  }

  const oldNewFeatureMap: Record<OldFeatureType, string | null> = {
    featureInfo: 'info',
    featureAnalytic: 'analytics',
    featureProjectAnalysis: 'project-analysis',
    featureWip: 'wip',
    '': null
  }

  // Getters
  const selectedAttachment = computed(() => {
    return selectedFileEntityId.value ? { entityId: selectedFileEntityId.value } : null
  })
  const showFeature = computed(() => {
    return mainFeatureName.value ? newOldFeatureMap[mainFeatureName.value] : ''
  })
  const showFeatureAnalytic = computed(() => {
    return ['analytics', 'assets', 'wip'].includes(mainFeatureName.value)
  })
  const showFeatureProjectAnalysis = computed(() => {
    return mainFeatureName.value === 'project-analysis'
  })
  const showFeatureErrands = computed(() => {
    return mainFeatureName.value === 'errands'
  })
  const showFeatureInfo = computed(() => {
    return mainFeatureName.value === 'info'
  })
  const showNoFeature = computed(() => {
    return hideFeature.value
  })
  const showConnectionPopup = computed(() => {
    return connectionPopup.value
  })
  // Actions
  function setCurrentOverlayModalPinia(modalName: string) {
    currentOverlayModalPinia.value = modalName
  }
  function setCurrentOverlayModalOnModalPinia(modalName: string) {
    currentOverlayModalOnModalPinia.value = modalName
  }
  function setFilteredDrawings(drawings: Drawing[]) {
    filteredDrawings.value = drawings
  }
  function setFilteredLines(lines: Line[]) {
    filteredLineLayers.value = lines
  }
  function setFilteredOrthophotos(orthophotos: Orthophoto[]) {
    filteredOrthophotos.value = orthophotos
  }
  function setFilteredWmsLayers(wmss: any[]) {
    filteredWmsLayers.value = wmss
  }
  function setIsFeatureFullscreen(isFullscreen: boolean) {
    isFeatureFullscreen.value = isFullscreen
  }
  function setLineCategoriesForRealEstate(categories: LineCategoryDto[]) {
    lineCategoriesForSelectedRealEstate.value = categories
  }
  function setLineDataForCoreEntity(lines: LineGeoJsonFeature[]) {
    linesForSelectedRealEstate.value = lines
  }
  function setRightClickCoordinate(coordinate: Array<number>) {
    rightClickCoordinate.value = coordinate
  }
  function setSelectedAnalysisKPI(kpi: string | undefined) {
    selectedAnalysisKPI.value = kpi
  }
  function setSelectedFileEntityId(entityId: string | null) {
    selectedFileEntityId.value = entityId
  }
  function setSelectedLine(line: LineGeoJsonProperties | null) {
    selectedLine.value = line
    selectedLineLayerId.value = line?.layerId ?? null
  }
  function setSelectedTicket(ticket: Ticket | null) {
    selectedTicket.value = ticket
  }
  function setSelectedZone(zone: ZoneProperties | null) {
    selectedZone.value = zone
    selectedZoneLayerId.value = zone?.layerId ?? null
  }
  function setShowLineMenu(showMenu: boolean) {
    showLineMenu.value = showMenu
  }
  function setShowOverlayModalPinia(showModal: boolean) {
    showOverlayModalPinia.value = showModal
  }
  function setShowOverlayModalOnModalPinia(showModal: boolean) {
    showOverlayModalOnModalPinia.value = showModal
  }
  function setShowFeature(showFeatureName: OldFeatureType) {
    const feature = oldNewFeatureMap[showFeatureName as OldFeatureType]
    if (feature) setMainFeature(feature)
  }
  function setTicketsList(ticketsArray: Ticket[]) {
    ticketsList.value = ticketsArray
  }
  function setShowNoFeature(active: boolean) {
    hideFeature.value = active
  }
  function setShowConnectionPopup(active: boolean) {
    connectionPopup.value = active
  }
  async function updateLineDataForCoreEntity(
    categoryId: string,
    typeId: string,
    coreId: string,
    checked: boolean
  ) {
    const filteredLines = [...filteredLineLayers.value]
    const isTypeDataAlreadyFetched = linesForSelectedRealEstate.value.some(
      (feature) => feature.properties.typeId === typeId
    )
    if (!isTypeDataAlreadyFetched) {
      const newLines = await addLineFeaturesForType(
        coreId,
        categoryId,
        typeId,
        linesForSelectedRealEstate.value
      )
      if (!newLines?.length) return
      setLineDataForCoreEntity(newLines)
      setNewLineDataVisibility(categoryId, typeId, checked, filteredLines)
    } else {
      setExistingLineDataVisibility(typeId, checked, filteredLines)
    }
    setFilteredLines(filteredLines)
  }
  return {
    currentOverlayModalPinia,
    currentOverlayModalOnModalPinia,
    filteredDrawings,
    filteredLineLayers,
    filteredOrthophotos,
    filteredWmsLayers,
    isFeatureFullscreen,
    linesForSelectedRealEstate,
    lineCategoriesForSelectedRealEstate,
    rightClickCoordinate,
    selectedAnalysisKPI,
    selectedAttachment,
    selectedFileEntityId,
    selectedLine,
    selectedLineLayerId,
    selectedTicket,
    selectedZone,
    selectedZoneLayerId,
    setCurrentOverlayModalPinia,
    setCurrentOverlayModalOnModalPinia,
    setFilteredDrawings,
    setFilteredLines,
    setFilteredOrthophotos,
    setFilteredWmsLayers,
    setIsFeatureFullscreen,
    setLineCategoriesForRealEstate,
    setLineDataForCoreEntity,
    setRightClickCoordinate,
    setSelectedAnalysisKPI,
    setSelectedFileEntityId,
    setSelectedLine,
    setSelectedTicket,
    setSelectedZone,
    setShowLineMenu,
    setShowOverlayModalPinia,
    setShowOverlayModalOnModalPinia,
    setShowFeature,
    setShowNoFeature,
    setTicketsList,
    setShowConnectionPopup,
    showConnectionPopup,
    showFeature,
    showFeatureAnalytic,
    showFeatureProjectAnalysis,
    showFeatureErrands,
    showFeatureInfo,
    showLineMenu,
    showNoFeature,
    showOverlayModalPinia,
    showOverlayModalOnModalPinia,
    ticketsList,
    updateLineDataForCoreEntity
  }
})
